import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { wsClient } from '../../services/websocket-client.js';

type VoiceInputMode = 'push-to-talk' | 'auto-detect' | 'continuous';

interface VoiceSettingsStatus {
  deepgramConfigured: boolean;
  elevenLabsConfigured: boolean;
  defaultInputMode: VoiceInputMode;
}

interface WsMessage<T = unknown> {
  type: string;
  payload: T;
}

@customElement('cp-voice-settings')
export class CpVoiceSettings extends LitElement {
  createRenderRoot() { return this; }

  @state() private _loading = true;
  @state() private _saving = false;
  @state() private _error = '';
  @state() private _successMessage = '';
  @state() private _deepgramKey = '';
  @state() private _elevenLabsKey = '';
  @state() private _inputMode: VoiceInputMode = 'push-to-talk';
  @state() private _deepgramConfigured = false;
  @state() private _elevenLabsConfigured = false;

  private _boundMessageHandler = this._onWsMessage.bind(this);

  connectedCallback(): void {
    super.connectedCallback();
    wsClient.addEventListener('ws-message', this._boundMessageHandler as EventListener);
    this._requestStatus();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    wsClient.removeEventListener('ws-message', this._boundMessageHandler as EventListener);
  }

  private _requestStatus(): void {
    this._loading = true;
    wsClient.send({
      type: 'settings.voice.get',
      payload: {},
      timestamp: new Date().toISOString(),
    });
    // Set a timeout fallback in case WebSocket is not connected
    setTimeout(() => {
      if (this._loading) {
        this._loading = false;
      }
    }, 3000);
  }

  private _onWsMessage(e: Event): void {
    const detail = (e as CustomEvent<WsMessage>).detail;

    switch (detail.type) {
      case 'settings.voice.status': {
        const payload = detail.payload as VoiceSettingsStatus;
        this._deepgramConfigured = payload.deepgramConfigured;
        this._elevenLabsConfigured = payload.elevenLabsConfigured;
        this._inputMode = payload.defaultInputMode;
        this._loading = false;
        break;
      }

      case 'settings.voice.updated': {
        const payload = detail.payload as { success: boolean; status: VoiceSettingsStatus };
        if (payload.success) {
          this._deepgramConfigured = payload.status.deepgramConfigured;
          this._elevenLabsConfigured = payload.status.elevenLabsConfigured;
          this._inputMode = payload.status.defaultInputMode;
          this._deepgramKey = '';
          this._elevenLabsKey = '';
          this._successMessage = 'Voice-Konfiguration gespeichert';
          this._saving = false;
        }
        break;
      }

      case 'settings.voice.error': {
        const payload = detail.payload as { error: string };
        this._error = payload.error;
        this._saving = false;
        this._loading = false;
        break;
      }
    }
  }

  private _onDeepgramInput(e: Event): void {
    this._deepgramKey = (e.target as HTMLInputElement).value;
    this._successMessage = '';
  }

  private _onElevenLabsInput(e: Event): void {
    this._elevenLabsKey = (e.target as HTMLInputElement).value;
    this._successMessage = '';
  }

  private _onInputModeChange(e: Event): void {
    this._inputMode = (e.target as HTMLSelectElement).value as VoiceInputMode;
    this._successMessage = '';
  }

  private _save(): void {
    if (this._saving) return;

    const payload: Record<string, string> = {};
    if (this._deepgramKey.trim()) {
      payload.deepgramApiKey = this._deepgramKey.trim();
    }
    if (this._elevenLabsKey.trim()) {
      payload.elevenLabsApiKey = this._elevenLabsKey.trim();
    }
    payload.defaultInputMode = this._inputMode;

    this._saving = true;
    this._error = '';
    this._successMessage = '';

    wsClient.send({
      type: 'settings.voice.update',
      payload,
      timestamp: new Date().toISOString(),
    });
  }

  private _onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      this._save();
    }
  }

  private _renderStatus() {
    const configured = this._deepgramConfigured && this._elevenLabsConfigured;
    if (configured) {
      return html`<span class="cp-voice__status cp-voice__status--configured">Konfiguriert</span>`;
    }
    return html`<span class="cp-voice__status cp-voice__status--not-configured">Nicht konfiguriert</span>`;
  }

  render() {
    return html`
      <div class="cp-voice">
        <div class="cp-voice__header">
          <h3 class="cp-voice__title">Voice</h3>
          ${this._renderStatus()}
        </div>

        ${this._error ? html`
          <div class="cp-voice__error">${this._error}</div>
        ` : nothing}

        ${this._successMessage ? html`
          <div class="cp-voice__success">${this._successMessage}</div>
        ` : nothing}

        ${this._loading ? html`
          <p class="cp-voice__loading">Konfiguration wird geladen...</p>
        ` : html`
          <div class="cp-voice__services">
            <div class="cp-voice__service-row">
              <span class="cp-voice__service-name">Deepgram (STT)</span>
              ${this._deepgramConfigured
                ? html`<span class="cp-voice__service-badge cp-voice__service-badge--ok">Konfiguriert</span>`
                : html`<span class="cp-voice__service-badge cp-voice__service-badge--missing">Nicht konfiguriert</span>`}
            </div>
            <div class="cp-voice__service-row">
              <span class="cp-voice__service-name">ElevenLabs (TTS)</span>
              ${this._elevenLabsConfigured
                ? html`<span class="cp-voice__service-badge cp-voice__service-badge--ok">Konfiguriert</span>`
                : html`<span class="cp-voice__service-badge cp-voice__service-badge--missing">Nicht konfiguriert</span>`}
            </div>
          </div>

          <div class="cp-voice__form">
            <div class="cp-voice__field">
              <label class="cp-voice__label" for="voice-deepgram-key">Deepgram API Key</label>
              <input
                id="voice-deepgram-key"
                class="cp-voice__input"
                type="password"
                placeholder=${this._deepgramConfigured ? 'Bereits konfiguriert - leer lassen um beizubehalten' : 'API Key eingeben'}
                .value=${this._deepgramKey}
                @input=${this._onDeepgramInput}
                @keydown=${this._onKeyDown}
              />
            </div>

            <div class="cp-voice__field">
              <label class="cp-voice__label" for="voice-elevenlabs-key">ElevenLabs API Key</label>
              <input
                id="voice-elevenlabs-key"
                class="cp-voice__input"
                type="password"
                placeholder=${this._elevenLabsConfigured ? 'Bereits konfiguriert - leer lassen um beizubehalten' : 'API Key eingeben'}
                .value=${this._elevenLabsKey}
                @input=${this._onElevenLabsInput}
                @keydown=${this._onKeyDown}
              />
            </div>

            <div class="cp-voice__field">
              <label class="cp-voice__label" for="voice-input-mode">Standard Input-Modus</label>
              <select
                id="voice-input-mode"
                class="cp-voice__select"
                .value=${this._inputMode}
                @change=${this._onInputModeChange}
              >
                <option value="push-to-talk" ?selected=${this._inputMode === 'push-to-talk'}>Push-to-Talk</option>
                <option value="auto-detect" ?selected=${this._inputMode === 'auto-detect'}>Auto-Detect</option>
                <option value="continuous" ?selected=${this._inputMode === 'continuous'}>Continuous</option>
              </select>
            </div>

            <button
              class="cp-voice__btn cp-voice__btn--save"
              @click=${this._save}
              ?disabled=${this._saving}
            >${this._saving ? 'Speichert...' : 'Speichern'}</button>
          </div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-voice-settings': CpVoiceSettings;
  }
}
